import type { Address } from '@medplum/fhirtypes'

/*
 * How a postal address fared against Google's Address Validation API (with
 * USPS CASS for US addresses):
 * - valid: deliverable exactly as entered.
 * - corrected: deliverable, but Google replaced, spell-corrected or filled in a
 *   component; `standardized` is the address to save.
 * - missingUnit: the building is found but needs an apartment/suite number.
 * - unconfirmed: plausible, but some components could not be confirmed.
 * - invalid: Google could not resolve a deliverable address.
 */
export const ADDRESS_VALIDATION_STATUSES = [
  'valid',
  'corrected',
  'missingUnit',
  'unconfirmed',
  'invalid',
] as const

// The part of a FHIR Address that postal validation reads and returns. A plain
// subset (no extensions) so it serializes through typed RPC responses.
export interface PostalAddressFields {
  line?: string[]
  city?: string
  state?: string
  postalCode?: string
  country?: string
}

export type AddressValidationStatus =
  (typeof ADDRESS_VALIDATION_STATUSES)[number]

export interface AddressValidationResult {
  status: AddressValidationStatus
  // Google's standardized form of the address. Absent when Google could not
  // resolve one (most `invalid` results).
  standardized?: PostalAddressFields
  formattedAddress?: string
  // Google component types, e.g. `subpremise`, `postal_code`, `route`.
  missingComponents: string[]
  unconfirmedComponents: string[]
  // USPS delivery-point confirmation: Y (confirmed), S/D (secondary unit
  // missing or unconfirmed), N (not deliverable). Absent outside the US.
  uspsDpvConfirmation?: string
}

// Google `google.type.PostalAddress`; only the fields read here.
export interface GooglePostalAddress {
  regionCode?: string
  postalCode?: string
  administrativeArea?: string
  locality?: string
  addressLines?: string[]
}

// Google Address Validation `validateAddress` response; only the fields read
// here. Every field is optional because Google omits false/empty values.
export interface GoogleValidateAddressResponse {
  result?: {
    verdict?: {
      inputGranularity?: string
      validationGranularity?: string
      addressComplete?: boolean
      hasUnconfirmedComponents?: boolean
      hasInferredComponents?: boolean
      hasReplacedComponents?: boolean
      hasSpellCorrectedComponents?: boolean
      possibleNextAction?: string
    }
    address?: {
      formattedAddress?: string
      postalAddress?: GooglePostalAddress
      addressComponents?: {
        componentName?: { text?: string }
        componentType?: string
      }[]
      missingComponentTypes?: string[]
      unconfirmedComponentTypes?: string[]
    }
    uspsData?: {
      dpvConfirmation?: string
    }
  }
}

// Granularities too coarse to deliver mail to.
const UNDELIVERABLE_GRANULARITIES = new Set([
  'GRANULARITY_UNSPECIFIED',
  'OTHER',
  'BLOCK',
  'ROUTE',
])

const US_COUNTRY_NAMES = new Set([
  'US',
  'USA',
  'UNITED STATES',
  'UNITED STATES OF AMERICA',
])

export function isUsAddress(address: Address): boolean {
  return (
    !address.country ||
    US_COUNTRY_NAMES.has(address.country.trim().toUpperCase())
  )
}

function nonEmpty(
  values: readonly (string | undefined)[] | undefined
): string[] {
  return (values ?? []).map((value) => value?.trim() ?? '').filter(Boolean)
}

// A free-text-only address goes to Google as a single line; Google parses it.
function validatableLines(address: Address): string[] {
  const lines = nonEmpty(address.line)
  return lines.length > 0 ? lines : nonEmpty([address.text])
}

export function hasValidatableContent(address: Address): boolean {
  return validatableLines(address).length > 0
}

export function fhirAddressToGooglePostalAddress(
  address: Address
): GooglePostalAddress {
  return {
    regionCode: 'US',
    addressLines: validatableLines(address),
    locality: address.city?.trim() || undefined,
    administrativeArea: address.state?.trim() || undefined,
    postalCode: address.postalCode?.trim() || undefined,
  }
}

// Seen stores the five-digit ZIP; ZIP+4 from USPS is dropped so existing
// ZIP-prefix comparisons keep working.
function fiveDigitZip(postalCode: string | undefined): string | undefined {
  return postalCode?.trim().slice(0, 5) || undefined
}

// Google writes the unit onto the street line ("1839 W Valley Blvd PMB 76247").
// Seen keeps it on line 2, so line 1 is the building alone and "same building"
// is an exact comparison of line 1, city and ZIP.
function splitUnitLine(lines: string[], unit: string | undefined): string[] {
  const [street, ...rest] = lines
  if (!unit || !street || rest.length > 0 || !street.endsWith(unit)) {
    return lines
  }
  const building = street.slice(0, -unit.length).replace(/[\s,]+$/, '')
  return building ? [building, unit] : lines
}

export function googlePostalAddressToFhir(
  postalAddress: GooglePostalAddress,
  unit?: string
): PostalAddressFields {
  return {
    line: splitUnitLine(nonEmpty(postalAddress.addressLines), unit?.trim()),
    city: postalAddress.locality,
    state: postalAddress.administrativeArea,
    postalCode: fiveDigitZip(postalAddress.postalCode),
    country: postalAddress.regionCode,
  }
}

function classify(
  verdict: NonNullable<GoogleValidateAddressResponse['result']>['verdict'],
  missingComponents: string[]
): AddressValidationStatus {
  const changed =
    !!verdict?.hasReplacedComponents ||
    !!verdict?.hasSpellCorrectedComponents ||
    !!verdict?.hasInferredComponents

  switch (verdict?.possibleNextAction) {
    case 'FIX':
      return 'invalid'
    case 'CONFIRM_ADD_SUBPREMISES':
      return 'missingUnit'
    case 'CONFIRM':
      return changed ? 'corrected' : 'unconfirmed'
    case 'ACCEPT':
      return changed ? 'corrected' : 'valid'
  }

  if (
    !verdict?.addressComplete ||
    UNDELIVERABLE_GRANULARITIES.has(
      verdict.validationGranularity ?? 'GRANULARITY_UNSPECIFIED'
    )
  ) {
    return missingComponents.includes('subpremise') ? 'missingUnit' : 'invalid'
  }
  if (verdict.hasUnconfirmedComponents) {
    return 'unconfirmed'
  }
  return changed ? 'corrected' : 'valid'
}

export function interpretGoogleValidation(
  response: GoogleValidateAddressResponse
): AddressValidationResult {
  const result = response.result
  const missingComponents = result?.address?.missingComponentTypes ?? []
  const status = classify(result?.verdict, missingComponents)
  const postalAddress = result?.address?.postalAddress
  const unit = result?.address?.addressComponents?.find(
    (component) => component.componentType === 'subpremise'
  )?.componentName?.text
  return {
    status,
    standardized:
      postalAddress && status !== 'invalid'
        ? googlePostalAddressToFhir(postalAddress, unit)
        : undefined,
    formattedAddress: result?.address?.formattedAddress,
    missingComponents,
    unconfirmedComponents: result?.address?.unconfirmedComponentTypes ?? [],
    uspsDpvConfirmation: result?.uspsData?.dpvConfirmation,
  }
}

// Google Places (New) address component; only the fields read here.
export interface GooglePlaceAddressComponent {
  longText?: string
  shortText?: string
  types?: string[]
}

export function placeAddressComponentsToFhir(
  components: readonly GooglePlaceAddressComponent[]
): PostalAddressFields {
  const find = (
    type: string,
    form: 'longText' | 'shortText' = 'longText'
  ): string | undefined =>
    components.find((component) => component.types?.includes(type))?.[form]

  const street = nonEmpty([
    find('street_number'),
    find('route', 'shortText'),
  ]).join(' ')
  const unit = find('subpremise')
  return {
    line: nonEmpty([
      street || find('premise'),
      unit && /^\d/.test(unit) ? `#${unit}` : unit,
    ]),
    city:
      find('locality') ?? find('postal_town') ?? find('sublocality_level_1'),
    state: find('administrative_area_level_1', 'shortText'),
    postalCode: fiveDigitZip(find('postal_code')),
    country: find('country', 'shortText'),
  }
}

// The stored address rewritten in Google's standardized form. Fields the
// standardized form doesn't cover (use, type, period, extensions) are kept;
// `text` is dropped because it would no longer match the lines.
export function withStandardizedAddress(
  original: Address,
  standardized: PostalAddressFields
): Address {
  const { text: _staleText, ...rest } = original
  return {
    ...rest,
    line: standardized.line,
    city: standardized.city,
    state: standardized.state,
    postalCode: standardized.postalCode,
    country: standardized.country ?? original.country,
  }
}
